import { AxiosResponse } from "axios";
import { ConfigApi } from "./base/ConfigApi";
import MobileWebApiClient from "./base/MobileWebApiClient";

export type EmbedUrlResult =
    | { isSuccess: true; url: string }
    | { isSuccess: false; message: string };

export type GuestTokenResult =
    | { isSuccess: true; token: string }
    | { isSuccess: false; message: string; status: number };

const FORBIDDEN_MESSAGE = "You don't have access to this community's reports.";
const UNAUTHORIZED_MESSAGE = "Your session has expired. Please sign in again.";
const UNAVAILABLE_MESSAGE = "Reports are temporarily unavailable. Please try again shortly.";
export const GENERIC_MESSAGE = "Something went wrong loading this report. Please try again later.";

const messageForStatus = (status: number): string => {
    switch (status) {
        case 401:
            return UNAUTHORIZED_MESSAGE;
        case 403:
            return FORBIDDEN_MESSAGE;
        case 503:
            return UNAVAILABLE_MESSAGE;
        default:
            return GENERIC_MESSAGE;
    }
};

export default class ReportClient extends MobileWebApiClient{
    reportAndDashboardUrl: string;
    constructor(configApi: ConfigApi) {
         super(configApi);

        const raw = process.env.REACT_APP_SUPERSET_URL;
        if (!raw) {
            console.error("REACT_APP_SUPERSET_URL is not set");
        }
        this.reportAndDashboardUrl = raw ? (raw.endsWith("/") ? raw : raw + "/") : "";
    }

    /**
     * Asks the All Access API for a signed Metabase embed URL. The API checks the
     * caller's membership of `community` and does the signing server-side, so no
     * embedding secret reaches the browser.
     */
    public async getEmbedUrl(reportId: string, community: string): Promise<EmbedUrlResult> {
        if (!reportId || !community) {
            return { isSuccess: false, message: GENERIC_MESSAGE };
        }

        const res = await this.tryGetWithStatus(
            `/Reports/EmbedUrl?reportId=${encodeURIComponent(reportId)}&community=${encodeURIComponent(community)}`
        );

        if (res.ok && res.data?.url) {
            return { isSuccess: true, url: res.data.url };
        }
        return { isSuccess: false, message: messageForStatus(res.status) };
    }

    /**
     * Asks the All Access API to mint a Superset guest token. 
     */
    public async getGuestToken(dashboardId: string, community: string): Promise<GuestTokenResult> {
        if (!dashboardId || !community) {
            return { isSuccess: false, message: GENERIC_MESSAGE, status: 0 };
        }

        const res = await this.tryGetWithStatus(
            `/Reports/GuestToken?dashboardId=${encodeURIComponent(dashboardId)}&community=${encodeURIComponent(community)}`
        );

        if (res.ok && typeof res.data?.token === "string") {
            return { isSuccess: true, token: res.data.token };
        }
        return { isSuccess: false, message: messageForStatus(res.status), status: res.status };
    }

    public async PaidInvoiceDownload(id: string): Promise<AxiosResponse<Blob> | null> {
        try {
          const response = await this.get(`/PaidInvoiceReport/Download?id=${id}`,'blob');
            if (!response || !response.data) {
                console.error(`An error has occurred: ${response.status} - ${response.statusText}`);
                return null;
            }

            return response;
        } catch (error: any) {
            console.error(`An error has occurred: ${error.message}`);
            return null;
        }
    }
}
